import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import { LoanOffer } from 'embed/constants/models'

import Modal from 'embed/components/Modal'

import { useTheme } from 'embed/hooks'

const compactStyles = require('embed/styles/resultscompact')
const styles = require('embed/styles/results')

interface DisclaimerProps {
  el?: HTMLLIElement
  hasMoreOffers?: boolean
  isFilteredByLender?: boolean
  offer: LoanOffer
  offerStyleIsCompact: boolean
  seeMoreFromLender: () => void
  showSeeMoreOffers?: boolean
}

const negateValue = (value: boolean) => !value

export const DisclaimerModal: React.FC<DisclaimerProps> = ({
  el,
  hasMoreOffers,
  isFilteredByLender,
  offer,
  offerStyleIsCompact,
  seeMoreFromLender,
  showSeeMoreOffers,
}) => {
  const [disclaimerModalOffset, setDisclaimerModalOffset] = React.useState(0)
  const [disclaimerModalVisible, setDisclaimerModalVisible] = React.useState(false)
  const { colorPrimary, colorTextPrimary, colorTextSecondary, fontFamilyPrimary } = useTheme()
  const disclaimerDetails = offer?.originator?.description
  const disclaimerMoreInfo = offer?.originator?.disclaimer
  const toggleDisclaimerModal = React.useCallback(
    (event: React.MouseEvent<Element>) => {
      event.stopPropagation()
      setDisclaimerModalOffset(el?.offsetTop ?? 0)
      setDisclaimerModalVisible(negateValue)
    },
    [el, setDisclaimerModalOffset, setDisclaimerModalVisible],
  )

  // Only show 'see more offers by lender' label if there are more offers and we haven't already filtered
  const shouldShowSeeMoreOffers = showSeeMoreOffers && hasMoreOffers && !isFilteredByLender

  return (
    <>
      <p
        data-test-id="offer_openModal"
        className={classnames(
          styles.disclaimerLink,
          { [compactStyles.disclaimerLink]: offerStyleIsCompact },
          css`
            color: ${colorTextSecondary};
            &:hover {
              color: ${colorTextPrimary};
            }
          `,
        )}
        onClick={toggleDisclaimerModal}
      >
        Disclaimer &amp; more info
      </p>
      {!!disclaimerModalVisible && (
        <Modal offset={disclaimerModalOffset} onDismiss={toggleDisclaimerModal}>
          {disclaimerDetails && (
            <div className={styles.modalDisclaimer}>
              <h4
                className={css`
                  font-family: ${fontFamilyPrimary};
                `}
              >
                More Information
              </h4>
              <p data-test-id="offer_moreInfo" dangerouslySetInnerHTML={{ __html: disclaimerDetails }} />
            </div>
          )}
          {disclaimerMoreInfo && (
            <div className={styles.modalDisclaimer}>
              <h4
                className={css`
                  font-family: ${fontFamilyPrimary};
                `}
              >
                Disclaimer
              </h4>
              <p data-test-id="offer_disclaimerText" dangerouslySetInnerHTML={{ __html: disclaimerMoreInfo }} />
            </div>
          )}
        </Modal>
      )}
      {shouldShowSeeMoreOffers && !offerStyleIsCompact && offer.originator?.name && (
        <p
          data-test-id="offer_seeAllByLender"
          className={classnames(
            styles.seeMoreFromLender,
            css`
              color: ${colorPrimary};
            `,
          )}
          onClick={seeMoreFromLender}
        >
          See all offers through {offer.originator.name}
        </p>
      )}
    </>
  )
}
